import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useArcTranslation, hasPermission } from 'hooks'
import { Card, Text, Stack, Group, Button, Box, Anchor, Badge, Tooltip } from 'components'
import type { ArcProject } from 'pages/ArcProjectsList/types'
import { STATUS_DISPLAY_MAP } from 'pages/ArcProjectsList/types'
import { getPlaceholderImage, getCategoryTextColor, getCategoryBackgroundColor } from 'pages/ArcProjectsList/utils'
import { ArcProjectCardActions } from 'pages/ArcProjectsList/components/ArcProjectCard/ArcProjectCardActions'
import { isDemoProjectCardId } from 'arcdisk'

interface ArcProjectCardProps {
  project: ArcProject
}

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const DEFAULT_STATUS_CONFIG = { color: 'gray', labelKey: 'arc_projects.status_draft' }

export const ArcProjectCard = ({ project }: ArcProjectCardProps) => {
  const { t } = useArcTranslation()

  const projectURL = `/projects/${project.id}`
  const statusConfig = STATUS_DISPLAY_MAP[project.status] || DEFAULT_STATUS_CONFIG

  // Tooltip only adds value when the clamped title is actually truncated
  const titleRef = useRef<HTMLDivElement>(null)
  const [isTitleTruncated, setIsTitleTruncated] = useState(false)
  useLayoutEffect(() => {
    const el = titleRef.current
    if (el) setIsTitleTruncated(el.scrollHeight > el.clientHeight + 1)
  }, [project.name])

  const summary = useMemo(() => {
    if (!project.synopsis) return ''
    return stripHtml(project.synopsis).trim()
  }, [project.synopsis])

  const gradesText = useMemo(() => {
    const names = project.projectGrades
      .filter(pg => pg?.grade?.name)
      .map(pg => pg.grade.name)
    return names.join(', ')
  }, [project.projectGrades])

  const hasGrade = gradesText.length > 0
  const hasClassSection = !!project.groupName
  const hasCategories = project.categories.length > 0

  return (
    <Card
      shadow='sm'
      withBorder
      radius='md'
      p={0}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 380 }}
    >
      {/* Cover Image */}
      <Box
        sx={{
          position: 'relative',
          height: 130,
          backgroundImage: `url(${getPlaceholderImage(project.id)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderTopLeftRadius: 'var(--mantine-radius-md)',
          borderTopRightRadius: 'var(--mantine-radius-md)',
          flexShrink: 0,
        }}
      >
        <Badge
          color={statusConfig.color}
          variant="light"
          size="lg"
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            fontSize: 13,
            fontWeight: 600,
            padding: '6px 12px',
          }}
        >
          {t(statusConfig.labelKey)}
        </Badge>
      </Box>

      {/* Content */}
      <Stack spacing={8} p='md' style={{ flex: 1 }}>
        {/* Project Name */}
        <Tooltip label={project.name} multiline maw={300} withinPortal disabled={!isTitleTruncated} openDelay={300}>
          {/* Router Link keeps the persistent Arc shell mounted; modified
              clicks (cmd/ctrl/middle) still open a new tab */}
          <Anchor component={Link} to={projectURL} color='violet'>
            <Text ref={titleRef} size='md' weight={600} lineClamp={2} sx={{ lineHeight: 1.35 }}>
              {project.name}
            </Text>
          </Anchor>
        </Tooltip>

        {/* Summary (no label, next to title) */}
        <Box style={{ flex: 1 }}>
          <Text size='sm' lineClamp={4} color={summary ? 'gray.7' : 'dimmed'} sx={{ lineHeight: 1.55 }}>
            {summary || '-'}
          </Text>
        </Box>

        {/* Categories (only when project carries categories, e.g. from library) */}
        {hasCategories && (
          <Group spacing={8}>
            {project.categories.map(category => (
              <Badge
                key={category.id}
                variant='filled'
                radius='sm'
                sx={{
                  backgroundColor: getCategoryBackgroundColor(category.color),
                  color: getCategoryTextColor(category.color),
                  textTransform: 'uppercase',
                  fontWeight: 700,
                }}
              >
                {category.name}
              </Badge>
            ))}
          </Group>
        )}

        {/* Grade + Class: side by side when there is room, stacked on narrow
            cards (flex-basis governs the break point) — never squeezed */}
        {(hasGrade || hasClassSection) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 8, alignItems: 'flex-start' }}>
            {hasGrade && (
              <Stack spacing={4} sx={{ flex: '1 1 120px', minWidth: 0 }}>
                <Text size='xs' color='dimmed' tt='uppercase' weight={600} sx={{ letterSpacing: 0.4, lineHeight: 1.2 }}>
                  {t('arc_projects.grade_label')}
                </Text>
                <Text size='sm' lineClamp={2}>{gradesText}</Text>
              </Stack>
            )}
            {hasClassSection && (
              <Stack spacing={4} sx={{ flex: '1 1 120px', minWidth: 0 }}>
                <Text size='xs' color='dimmed' tt='uppercase' weight={600} sx={{ letterSpacing: 0.4, lineHeight: 1.2 }}>
                  {t('arc_projects.group_label')}
                </Text>
                <Text size='sm' lineClamp={2}>{project.groupName}</Text>
              </Stack>
            )}
          </Box>
        )}
      </Stack>

      {/* Actions — normal flow at the bottom of the flex column so it can
          never overlap the content above it, whatever the card width */}
      <Box px='md' pb='md' pt={8} mt='auto'>
        <Group position='apart' noWrap>
          <Button
            component={Link}
            to={projectURL}
            variant='subtle'
            color='teal'
            radius='xl'
            size='sm'
            styles={theme => ({
              root: {
                height: 44,
                paddingLeft: 12,
                paddingRight: 10,
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: theme.colors.teal[0] },
              },
              label: { whiteSpace: 'nowrap' },
            })}
          >
            {t('arc_projects.view_project')}
          </Button>
          <ArcProjectCardActions
            project={project}
            canEdit={hasPermission(['tutor', 'manager']) && !isDemoProjectCardId(project.id)}
            hideViewButton
          />
        </Group>
      </Box>
    </Card>
  )
}
